import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Avatar from '../../components/ui/Avatar';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconUsers,
  IconShield,
  IconCalendar,
  IconSearch,
  IconPlus,
  IconAlertTriangle,
  IconCheck,
  IconClock,
} from '@tabler/icons-react';

const QUAL_TYPE_KEYS = {
  forklift: 'qual.short_forklift', sprayer: 'qual.short_sprayer', cscs: 'qual.short_cscs',
  driving: 'qual.short_driving', firstaid: 'qual.short_firstaid', chainsaw: 'qual.short_chainsaw',
  pesticide: 'qual.short_pesticide', telehandler: 'qual.short_telehandler', atv: 'qual.short_atv',
  other: 'qual.short_other',
};

export default function StaffList() {
  const { t, language } = useTranslation();
  const { orgId, userProfile, role } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('members');
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const siteId = userProfile?.siteId;

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [staffSnap, shiftsSnap] = await Promise.all([
          getDocs(collection(db, 'organisations', orgId, 'users')),
          getDocs(query(collection(db, 'organisations', orgId, 'shifts'), orderBy('date', 'desc'))),
        ]);

        const allStaff = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const enriched = await Promise.all(allStaff.map(async (member) => {
          const qualsSnap = await getDocs(collection(db, 'organisations', orgId, 'users', member.id, 'qualifications'));
          const today = new Date();
          const quals = qualsSnap.docs.map(d => {
            const q = { id: d.id, ...d.data() };
            if (!q.expiryDate) return { ...q, status: 'valid' };
            const expiry = new Date(q.expiryDate);
            const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
            const status = days < 0 ? 'expired' : days <= 30 ? 'expiring' : 'valid';
            return { ...q, status, daysUntilExpiry: days };
          });
          return { ...member, qualifications: quals };
        }));

        let filtered = role === 'manager' && siteId ? enriched.filter(u => u.siteId === siteId) : enriched;
        setStaff(filtered);

        let allShifts = shiftsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (role === 'manager' && siteId) allShifts = allShifts.filter(s => s.siteId === siteId);
        setShifts(allShifts);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, siteId, role]);

  const filteredStaff = staff.filter(m => {
    const q = search.toLowerCase();
    return !q || m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);
  });

  const expiredCount = staff.reduce((sum, m) => sum + (m.qualifications?.filter(q => q.status === 'expired').length || 0), 0);
  const expiringCount = staff.reduce((sum, m) => sum + (m.qualifications?.filter(q => q.status === 'expiring').length || 0), 0);
  const totalQuals = staff.reduce((sum, m) => sum + (m.qualifications?.length || 0), 0);

  const roleLabel = (r) => r === 'org_admin' ? t('role.admin') : r === 'manager' ? t('role.manager') : t('role.operator');
  const roleStyle = (r) => {
    if (r === 'org_admin') return { background: 'rgba(232,97,10,0.1)', color: '#993C1D' };
    if (r === 'manager') return { background: '#E6F1FB', color: '#185FA5' };
    return { background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' };
  };

  const memberStatus = (member) => {
    const quals = member.qualifications || [];
    const expired = quals.filter(q => q.status === 'expired').length;
    const expiring = quals.filter(q => q.status === 'expiring').length;
    if (expired) return { kind: 'danger', text: expired + ' ' + t('staff.n_expired'), icon: IconAlertTriangle };
    if (expiring) return { kind: 'warning', text: expiring + ' ' + t('staff.n_expiring'), icon: IconClock };
    if (quals.length) return { kind: 'ok', text: quals.length + ' ' + t('staff.n_valid'), icon: IconCheck };
    return { kind: 'none', text: t('common.none'), icon: null };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const TABS = [
    { key: 'members',        label: t('staff.tab_members'),        icon: IconUsers,    count: staff.length  },
    { key: 'qualifications', label: t('staff.tab_qualifications'), icon: IconShield,   count: totalQuals    },
    { key: 'shifts',         label: t('staff.tab_shifts'),         icon: IconCalendar, count: shifts.length },
  ];

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title={t('nav.staff')}
          subtitle={staff.length + ' ' + t(staff.length === 1 ? 'staff.member_one' : 'staff.member_many') + ' · ' + (expiredCount + expiringCount) + ' ' + t((expiredCount + expiringCount) === 1 ? 'staff.alert_one' : 'staff.alert_many')}
          actions={
            activeTab === 'members' ? (
              <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/staff/invite')}>
                {t('staff.invite')}
              </Button>
            ) : activeTab === 'shifts' ? (
              <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/shifts/new')}>
                {t('staff.create_shift')}
              </Button>
            ) : null
          }
        />

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '0.5px solid var(--color-border-light)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <div
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '10px 16px', fontSize: 13, cursor: 'pointer',
                  color: active ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                  borderBottom: active ? '2px solid var(--color-brand)' : '2px solid transparent',
                  marginBottom: -1, transition: 'all 0.15s',
                }}
              >
                <Icon size={14} stroke={1.5} />
                {tab.label}
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{tab.count}</span>
              </div>
            );
          })}
        </div>

        {/* MEMBERS TAB */}
        {activeTab === 'members' && (
          <div>
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <IconSearch size={14} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder={t('staff.search')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box', maxWidth: 320 }}
              />
            </div>

            {loading ? (
              <LoadingSkeleton rows={4} card />
            ) : filteredStaff.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<IconUsers size={32} stroke={1.5} />}
                  title={t('staff.none_found')}
                  message={search ? t('staff.adjust_search') : t('staff.invite_first')}
                  action={!search && (
                    <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/staff/invite')}>
                      {t('staff.invite')}
                    </Button>
                  )}
                />
              </Card>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {filteredStaff.map(member => {
                  const status = memberStatus(member);
                  const cardBg = status.kind === 'danger' ? 'var(--color-danger-light)'
                    : status.kind === 'warning' ? 'var(--color-warning-light)'
                    : 'var(--color-bg-primary)';
                  const cardBorder = status.kind === 'danger' ? '0.5px solid var(--color-danger)'
                    : status.kind === 'warning' ? '0.5px solid var(--color-warning)'
                    : '0.5px solid var(--color-border-light)';

                  const StatusIcon = status.icon;
                  const pillStyle = status.kind === 'danger' ? { background: 'var(--color-danger-light)', color: 'var(--color-danger)' }
                    : status.kind === 'warning' ? { background: 'var(--color-warning-light)', color: 'var(--color-warning)' }
                    : status.kind === 'ok' ? { background: 'var(--color-success-light)', color: 'var(--color-success)' }
                    : { background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' };

                  return (
                    <Card
                      key={member.id}
                      onClick={() => navigate('/staff/' + member.id)}
                      hover
                      style={{ textAlign: 'center', padding: '20px 12px 16px', background: cardBg, border: cardBorder }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                        <Avatar name={member.name} photoURL={member.photoURL} size="lg" />
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 4 }}>{member.name}</div>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, display: 'inline-block', marginBottom: 6, ...roleStyle(member.role) }}>
                        {roleLabel(member.role)}
                      </span>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
                        {member.siteName || (member.role === 'org_admin' ? t('machines.all_sites') : '—')}
                      </div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, padding: '2px 8px', borderRadius: 20, ...pillStyle }}>
                        {StatusIcon && <StatusIcon size={10} stroke={2} />}
                        {status.text}
                      </span>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* QUALIFICATIONS TAB */}
        {activeTab === 'qualifications' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: t('staff.stat_total_quals'), value: totalQuals, colour: 'var(--color-text-primary)' },
                { label: t('staff.stat_valid'), value: totalQuals - expiringCount - expiredCount, colour: 'var(--color-success)' },
                { label: t('staff.stat_expiring_30d'), value: expiringCount, colour: expiringCount > 0 ? 'var(--color-warning)' : 'var(--color-text-tertiary)' },
                { label: t('staff.stat_expired'), value: expiredCount, colour: expiredCount > 0 ? 'var(--color-danger)' : 'var(--color-text-tertiary)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
                  <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {staff.filter(m => m.qualifications?.length > 0).map(member => (
              <Card key={member.id} style={{ marginBottom: 10 }} onClick={() => navigate('/staff/' + member.id)} hover>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, paddingBottom: 10, borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <Avatar name={member.name} photoURL={member.photoURL} size="sm" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{member.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{member.qualifications.length} {t(member.qualifications.length === 1 ? 'staff.qual_one' : 'staff.qual_many')}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {member.qualifications.map(qual => (
                    <div key={qual.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0' }}>
                      <div style={{ fontSize: 12, color: qual.status === 'expired' ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
                        {QUAL_TYPE_KEYS[qual.type] ? t(QUAL_TYPE_KEYS[qual.type]) : qual.type}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {qual.expiryDate && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{t('staff.expires')} {formatDate(qual.expiryDate)}</div>}
                        <StatusPill status={qual.status} />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}

            {staff.filter(m => m.qualifications?.length > 0).length === 0 && !loading && (
              <Card>
                <EmptyState icon={<IconShield size={32} stroke={1.5} />} title={t('staff.no_quals')} message={t('staff.no_quals_msg')} />
              </Card>
            )}
          </div>
        )}

        {/* SHIFTS TAB */}
        {activeTab === 'shifts' && (
          <div>
            {loading ? (
              <LoadingSkeleton rows={4} card />
            ) : shifts.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<IconCalendar size={32} stroke={1.5} />}
                  title={t('staff.no_shifts')}
                  message={t('staff.no_shifts_msg')}
                  action={
                    <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/shifts/new')}>
                      {t('staff.create_shift')}
                    </Button>
                  }
                />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {shifts.map(shift => {
                  const checkedIn = shift.assignments?.filter(a => a.checkedIn).length || 0;
                  return (
                    <Card key={shift.id} onClick={() => navigate('/shifts/' + shift.id)} hover>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 3 }}>
                            {shift.siteName || t('staff.shift')} · {formatDate(shift.date)}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                            <span><IconClock size={11} stroke={1.5} style={{ verticalAlign: -1, marginRight: 3 }} />{shift.startTime} — {shift.endTime}</span>
                            <span><IconUsers size={11} stroke={1.5} style={{ verticalAlign: -1, marginRight: 3 }} />{checkedIn} / {shift.assignments?.length || 0} {t('staff.checked_in')}</span>
                          </div>
                        </div>
                        <StatusPill status={shift.status || 'scheduled'} />
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
}